<template>
  <div class="pl-day-board">
    <!-- Day pager -->
    <div class="flex align-items-center gap-sm border-top border-bottom p-y-xs">
      <button type="button" class="btn btn-default btn-compact" aria-label="Previous day" @click="shiftDay(-1)">
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <h2 class="text-16 font-semi m-y-none">{{ dayMoment.format('dddd, MMM D') }}</h2>
      <span v-if="isToday" class="pl-slot-badge bg-gray-100 text-gray-600">Today</span>
      <button v-else type="button" class="btn btn-default btn-compact" @click="goToToday">Today</button>
      <button type="button" class="btn btn-default btn-compact" aria-label="Next day" @click="shiftDay(1)">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div v-if="loading" class="text-center p-lg">
      <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
    </div>
    <template v-else>
      <div v-if="coachRows.length === 0" class="bg-gray-50 rounded-sm p-md text-center m-t-md">
        <p class="text-gray-600 m-b-none">No availability or lessons on {{ dayMoment.format('dddd, MMM D') }}.</p>
      </div>
      <template v-else>
        <!-- Legend (gap-x-md doesn't exist in the utility scale; m is 16px) -->
        <div class="flex flex-wrap gap-x-m gap-y-xxs m-t-sm">
          <span v-for="item in legendItems" :key="item.kind" class="flex align-items-center gap-x-xxs text-12 text-gray-600"><span class="pl-legend-swatch" :class="`is-${item.kind}`"></span>{{ item.label }}</span>
        </div>

        <!-- Coach rows on a shared time axis -->
        <div class="pl-day-scroll m-t-sm">
          <div class="pl-day-grid-min">
            <div class="pl-day-row">
              <div></div>
              <div class="pl-day-axis" :style="{ gridTemplateColumns: `repeat(${hourCount}, minmax(0, 1fr))` }">
                <span v-for="hour in hourLabels" :key="hour" class="text-12 text-gray-500 pl-slot-time">{{ hour }}</span>
              </div>
            </div>
            <div v-for="row in coachRows" :key="row.coachId" class="pl-day-row border-top">
              <p class="text-14 font-semi m-b-none pl-truncate p-y-sm">{{ row.coachName }}</p>
              <div class="pl-day-lane" :style="{ gridTemplateColumns: `repeat(${unitCount}, minmax(0, 1fr))` }">
                <div
                  v-for="chip in row.chips"
                  :key="chip.id"
                  class="pl-day-chip"
                  :class="`is-${chip.kind}`"
                  :style="{ gridColumn: `${chip.startUnit} / span ${chip.spanUnits}` }"
                  :title="chip.tooltip"
                  @click="viewEvent(chip.id)">
                  <span class="pl-truncate">{{ chip.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <div v-show="error" class="m-t-sm">
      <small><span class="text-danger">{{ errorMessage }}</span></small>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import { errorableMix } from './mixins/errorable_mix';
import { privateLessonStatusMix } from './mixins/private_lesson_status_mix';

// Availability board drill-down: every coach's slots for one day on a shared
// time axis. Half-hour units; the axis spans the day's earliest to latest slot.
export default {
  name: 'private-lesson-day-board',
  mixins: [errorableMix, privateLessonStatusMix],
  data() {
    return {
      loading: true,
      dayMoment: moment(),
      events: [],
    };
  },
  computed: {
    isToday() {
      return this.dayMoment.isSame(moment(), 'day');
    },
    slots() {
      return this.events
        .filter((event) => event.event_type === 'private_lesson')
        .map((event) => {
          const bookings = event.event_bookings || [];
          const coachBooking = bookings.find((b) => b.profile_type === 'CoachProfile');
          if (!coachBooking) {
            return null;
          }
          const wrestlers = bookings
            .filter((b) => b.profile_type === 'WrestlerProfile' && ['confirmed', 'completed', 'invited'].includes(b.status))
            .map((b) => (b.profile ? b.profile.display_name || b.profile.first_name : 'Wrestler'));
          return {
            id: event.id,
            coachId: coachBooking.profile_id,
            // Booking profiles serialize display_name (full name), not first/last
            coachName: coachBooking.profile ? coachBooking.profile.display_name : 'Coach',
            status: coachBooking.status,
            start: moment(event.start_at),
            end: moment(event.end_at),
            location: event.location,
            wrestlers,
          };
        })
        .filter((slot) => slot !== null && slot.status !== 'cancelled' && slot.status !== 'declined');
    },
    // Axis bounds in half-hour units, snapped to whole hours around the day's slots
    axisStartHour() {
      if (this.slots.length === 0) {
        return 8;
      }
      return Math.min(...this.slots.map((s) => s.start.hour()));
    },
    axisEndHour() {
      if (this.slots.length === 0) {
        return 20;
      }
      const end = Math.max(...this.slots.map((s) => s.end.hour() + (s.end.minute() > 0 ? 1 : 0)));
      // A minimum span keeps a single-slot day from rendering one wall-to-wall
      // chip — the chip should read as an hour, not the whole day
      return Math.min(Math.max(end, this.axisStartHour + 4), 24);
    },
    hourCount() {
      return this.axisEndHour - this.axisStartHour;
    },
    unitCount() {
      return this.hourCount * 2;
    },
    hourLabels() {
      const labels = [];
      for (let hour = this.axisStartHour; hour < this.axisEndHour; hour += 1) {
        labels.push(moment().hour(hour).minute(0).format('ha'));
      }
      return labels;
    },
    coachRows() {
      const byCoach = {};
      this.slots.forEach((slot) => {
        if (!byCoach[slot.coachId]) {
          byCoach[slot.coachId] = { coachId: slot.coachId, coachName: slot.coachName, chips: [] };
        }
        const startUnit = this.unitFor(slot.start) + 1;
        const spanUnits = Math.max(this.unitFor(slot.end) - this.unitFor(slot.start), 1);
        const kind = this.kindFor(slot.status);
        byCoach[slot.coachId].chips.push({
          id: slot.id,
          startUnit,
          spanUnits,
          kind,
          label: slot.wrestlers.length > 0 ? `${slot.start.format('h:mm')} · ${slot.wrestlers.join(', ')}` : slot.start.format('h:mm'),
          tooltip: `${slot.start.format('h:mm a')}–${slot.end.format('h:mm a')} · ${this.slotStatusLabel(slot.status)}${slot.wrestlers.length > 0 ? ` · ${slot.wrestlers.join(', ')}` : ''}${slot.location ? ` · ${slot.location}` : ''}`,
        });
      });
      return Object.values(byCoach).sort((a, b) => a.coachName.localeCompare(b.coachName));
    },
    // Derived from the same status mapping as the chips so legend and board
    // can't drift apart
    legendItems() {
      return ['pending', 'confirmed', 'invited', 'completed'].map((status) => ({
        kind: this.kindFor(status),
        label: this.slotStatusLabel(status),
      }));
    },
  },
  mounted() {
    this.fetchDay();
  },
  methods: {
    fetchDay() {
      const vm = this;
      vm.loading = true;
      const params = {
        event_type: 'private_lesson',
        start: vm.dayMoment.format('YYYY-MM-DD'),
        end: vm.dayMoment.format('YYYY-MM-DD'),
        expand: ['event_bookings'],
      };
      const url = vm.$apiService.eventsUrl(`?${qs.stringify(params, { arrayFormat: 'brackets' })}`);
      vm.$apiService.loadAllPages(url, 'events')
        .then((events) => {
          vm.events = events;
          vm.loading = false;
        })
        .catch((fetchError) => {
          vm.loading = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    shiftDay(delta) {
      this.dayMoment = moment(this.dayMoment).add(delta, 'day');
      this.fetchDay();
    },
    goToToday() {
      this.dayMoment = moment();
      this.fetchDay();
    },
    unitFor(timeMoment) {
      return Math.round(timeMoment.diff(moment(timeMoment).startOf('day').hour(this.axisStartHour), 'minutes') / 30);
    },
    // Status -> chip css kind; labels come from privateLessonStatusMix so this
    // board reads the same as the agenda and command center
    kindFor(status) {
      switch (status) {
        case 'pending': return 'open';
        case 'confirmed': return 'booked';
        case 'invited': return 'invited';
        case 'completed': return 'completed';
        default: return 'open';
      }
    },
    viewEvent(eventId) {
      window.location.assign(`/events/${eventId}`);
    },
  },
};
</script>
